import React, { useState } from 'react';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import {
  CreateFanTierInput,
  FanTierDto,
  useCreateFanTier,
  useDeleteFanTier,
  useFanTiers,
  useUpdateFanTier,
} from '@shared/api/hooks/useDiscountRules';

const { Text } = Typography;

const SUBSCRIPTION_TYPES = ['CURVA', 'LATERALE_DISTINTI', 'CENTRALE_VIP', 'POLTRONISSIMA'];

interface Values {
  key: string;
  nameIt: string;
  nameEn?: string;
  rank: number;
  minPoints: number;
  minSubscriptionType?: string;
  color?: string;
  isActive: boolean;
}

export function FanTiersPanel(): React.ReactElement {
  const { data: tiers = [], isLoading } = useFanTiers();
  const createM = useCreateFanTier();
  const updateM = useUpdateFanTier();
  const deleteM = useDeleteFanTier();
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form] = Form.useForm<Values>();

  const openCreate = (): void => {
    setEditId(null);
    form.resetFields();
    form.setFieldsValue({ rank: tiers.length, minPoints: 0, isActive: true });
    setOpen(true);
  };

  const openEdit = (t: FanTierDto): void => {
    setEditId(t.id);
    form.setFieldsValue({
      key: t.key,
      nameIt: t.name.it ?? t.key,
      nameEn: t.name.en,
      rank: t.rank,
      minPoints: t.minPoints,
      minSubscriptionType: t.minSubscriptionType ?? undefined,
      color: t.color ?? undefined,
      isActive: t.isActive,
    });
    setOpen(true);
  };

  const submit = async (): Promise<void> => {
    const v = await form.validateFields();
    const input: CreateFanTierInput = {
      key: v.key,
      name: { it: v.nameIt, ...(v.nameEn ? { en: v.nameEn } : {}) },
      rank: v.rank,
      minPoints: v.minPoints,
      minSubscriptionType: v.minSubscriptionType,
      color: v.color,
      isActive: v.isActive,
    };
    try {
      if (editId) {
        const { key: _key, ...rest } = input;
        await updateM.mutateAsync({ id: editId, input: rest });
      } else {
        await createM.mutateAsync(input);
      }
      message.success('Tier salvato');
      setOpen(false);
    } catch (e) {
      const msg =
        (e as { response?: { data?: { message?: string | string[] } } })?.response?.data?.message ??
        String(e);
      message.error(Array.isArray(msg) ? msg.join(' · ') : msg);
    }
  };

  return (
    <>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 12 }}
        message="Il tier di un tifoso e il MIGLIORE fra quello del suo abbonamento e quello dei suoi punti."
        description="I punti considerati sono quelli della STAGIONE in corso, non quelli accumulati a vita: altrimenti col passare degli anni tutti finirebbero nel tier massimo."
      />
      <Space style={{ marginBottom: 12 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
          Nuovo tier
        </Button>
      </Space>
      <Table
        rowKey="id"
        size="small"
        loading={isLoading}
        dataSource={[...tiers].sort((a, b) => a.rank - b.rank)}
        pagination={false}
        columns={[
          { title: 'Rank', dataIndex: 'rank', width: 70 },
          {
            title: 'Tier',
            dataIndex: 'key',
            render: (k: string, t: FanTierDto) => (
              <Space>
                <Tag color={t.color ?? 'default'}>{k}</Tag>
                <Text type="secondary">{t.name.it}</Text>
              </Space>
            ),
          },
          {
            title: 'Da punti stagione',
            dataIndex: 'minPoints',
            render: (v: number) => `>= ${v}`,
          },
          {
            title: 'Da abbonamento',
            dataIndex: 'minSubscriptionType',
            render: (v: string | null) => v ?? <Text type="secondary">—</Text>,
          },
          {
            title: 'Attivo',
            dataIndex: 'isActive',
            width: 80,
            render: (v: boolean) => (v ? <Tag color="green">si</Tag> : <Tag>no</Tag>),
          },
          {
            title: '',
            key: 'a',
            width: 100,
            render: (_: unknown, t: FanTierDto) => (
              <Space size={4}>
                <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(t)} />
                <Popconfirm
                  title="Eliminare il tier?"
                  description="Rifiutato se e usato da regole o assegnato come override a qualche tifoso."
                  onConfirm={async () => {
                    try {
                      await deleteM.mutateAsync(t.id);
                      message.success('Tier eliminato');
                    } catch (e) {
                      const msg =
                        (e as { response?: { data?: { message?: string } } })?.response?.data
                          ?.message ?? String(e);
                      message.error(msg);
                    }
                  }}
                >
                  <Button size="small" danger icon={<DeleteOutlined />} />
                </Popconfirm>
              </Space>
            ),
          },
        ]}
      />

      <Modal
        title={editId ? 'Modifica tier' : 'Nuovo tier'}
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => void submit()}
        confirmLoading={createM.isPending || updateM.isPending}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="key"
            label="Chiave"
            rules={[{ required: true }, { pattern: /^[A-Z][A-Z0-9_]{1,29}$/, message: 'MAIUSCOLO' }]}
            extra="Non modificabile: e l identita del tier, referenziata dalle regole."
          >
            <Input disabled={Boolean(editId)} placeholder="ORO" />
          </Form.Item>
          <Form.Item name="nameIt" label="Nome (it)" rules={[{ required: true }]}>
            <Input placeholder="Oro" />
          </Form.Item>
          <Form.Item name="nameEn" label="Nome (en)">
            <Input placeholder="Gold" />
          </Form.Item>
          <Form.Item
            name="rank"
            label="Rank"
            rules={[{ required: true }]}
            extra="0 = tier piu basso. Deve essere unico: e l ordinamento."
          >
            <InputNumber min={0} max={99} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="minPoints" label="Punti di stagione minimi" rules={[{ required: true }]}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="minSubscriptionType"
            label="Abbonamento che da diritto a questo tier"
            extra="Fa da pavimento: chi ha questo abbonamento ottiene il tier anche a zero punti."
          >
            <Select
              allowClear
              placeholder="nessuno"
              options={SUBSCRIPTION_TYPES.map((s) => ({ value: s, label: s }))}
            />
          </Form.Item>
          <Form.Item name="color" label="Colore" extra="Esadecimale, es. #D4AF37">
            <Input placeholder="#D4AF37" />
          </Form.Item>
          <Form.Item name="isActive" label="Attivo" valuePropName="checked">
            <Switch />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
